/**
 * @file resolveDesktopPinEntries.ts
 * @description Finds every `desktopPins` entry naming one pin path, oldest
 * first. Consumers: `resolveDesktopPinWrite`, `useDesktopPin` and
 * `useDesktopPinHolders`, which must all agree on which entry speaks for a path
 * when the map holds more than one.
 *
 * The map is keyed by pin id, not by path, so a path is found by scanning — that
 * is settled decision 4, and it follows from `DesktopPin`'s shape not changing.
 * Duplicates are therefore representable, and this returns all of them: the
 * first is the entry a reader should believe and a `keep` should reuse, the rest
 * are what an unpin (or the next `keep`) removes.
 *
 * Ordering is by `meta.created` ascending, so the survivor carries the true
 * creation time. An unreadable `created` — absent, or an unresolved
 * `serverTimestamp()` delivered as `null` — sorts last rather than first, and
 * pin id breaks ties so the order never depends on object key iteration.
 *
 * An id that cannot be addressed by a Firestore field path is skipped, because
 * every entry returned here is one a write may name as `desktopPins.{pinId}`.
 * The web SDK's dot-separated parser (`fieldPathFromDotSeparatedString`) throws
 * on `~ * / [ ]` and, worse, splits silently on `.` — `desktopPins.a.b` would
 * rewrite a nested node instead of the entry. Skipping makes such an entry
 * inert rather than destructive: `resolveDesktopPinWrite` still counts its
 * `artifactPath` towards `desktopPinPaths`, so the projection stays truthful,
 * but no client write can reuse or remove it. Nothing in this repo can mint one
 * — `keep` is the only writer and it uses `uuidv4`.
 */

import { Timestamp } from 'firebase/firestore';

import type { DesktopPin } from '#types';

export type DesktopPinEntry = {
    /** Key of the entry in `User.desktopPins`. */
    pinId: string;
    pin: DesktopPin;
};

const FIELD_PATH_UNSAFE = /[~*/[\].]/;

const createdMillis = (pin: DesktopPin): number => {
    const created = pin.meta?.created;
    return created instanceof Timestamp ? created.toMillis() : Number.POSITIVE_INFINITY;
};

export const resolveDesktopPinEntries = (
    pins: Readonly<Record<string, DesktopPin | undefined>> | undefined,
    pinPath: string,
): readonly DesktopPinEntry[] => {
    if (!pins) {
        return [];
    }

    return Object.entries(pins)
        .flatMap(([pinId, pin]) => (
            pin && pin.artifactPath === pinPath && pinId.length > 0 && !FIELD_PATH_UNSAFE.test(pinId)
                ? [{ pinId, pin }]
                : []
        ))
        .sort((a, b) => createdMillis(a.pin) - createdMillis(b.pin) || a.pinId.localeCompare(b.pinId));
};
